import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiClient } from '../../auth-lib';
import { Button, Card, Input, Modal, Select, Skeleton, useToast } from '../../design-system';
import WorkCalendar from '../../org-admin/components/attendance/WorkCalendar';
import { buildMonthOverlay, LEAVE_STATUS, fmtRange, fmtDay } from './monthOverlay';
import '../../org-admin/styles/Attendance.css';
import { useLiveRefresh } from '../../lib/useLiveRefresh';

// The employee's own month, and their leave.
//
// The calendar is the SAME component the settings screen draws — it just gets
// an overlay. Hovering the 5th says "Sick leave — approved · full day"; a
// holiday says its name; a future application says it is still awaiting a
// decision. That is the whole ask, and it costs one prop.

const todayISO = () => new Date().toISOString().slice(0, 10);
const currentYm = () => todayISO().slice(0, 7);

const PORTIONS = [
  { value: 'full', label: 'Full day' },
  { value: 'first_half', label: 'First half' },
  { value: 'second_half', label: 'Second half' },
];

function MyLeave({ refreshKey = 0 }) {
  const toast = useToast();
  const revision = useLiveRefresh();
  const [ym, setYm] = useState(currentYm);
  const [settings, setSettings] = useState(null);
  const [month, setMonth] = useState(null);
  const [types, setTypes] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  // ⚠️ `?apply=leave` OPENS THE FORM ON ARRIVAL. The day-start prompt sends
  // people here when they answer "I'm on leave" — it deliberately does not
  // mark the day itself, because leave runs through an approver, a balance and
  // a quota year, and a dialog that quietly set today to "leave" would leave
  // the register and the leave ledger disagreeing with no application between
  // them. Landing somebody on the calendar with the form still behind a button
  // would make that hand-off a suggestion rather than a route.
  const [searchParams, setSearchParams] = useSearchParams();
  const [applyOpen, setApplyOpen] = useState(() => searchParams.get('apply') === 'leave');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    leaveTypeId: '', fromDate: todayISO(), toDate: todayISO(), portion: 'full', reason: '',
  });

  // Spent once honoured, so closing the form and refreshing — or coming back
  // with the Back button — does not reopen it.
  useEffect(() => {
    if (searchParams.get('apply') !== 'leave') return;
    const next = new URLSearchParams(searchParams);
    next.delete('apply');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, m, t, r] = await Promise.all([
        apiClient.get('/api/attendance/settings'),
        apiClient.get('/api/attendance/me', { params: { month: ym } }),
        apiClient.get('/api/attendance/leave/types'),
        apiClient.get('/api/attendance/leave/mine'),
      ]);
      setSettings(s.data.settings);
      setMonth(m.data);
      setTypes(t.data);
      setRequests(r.data.rows);
      setForm((f) => ({ ...f, leaveTypeId: f.leaveTypeId || t.data.balances[0]?.typeId || '' }));
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not load your leave');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ym, refreshKey, revision]);
  useEffect(() => { load(); }, [load]);

  // Pending requests are laid on top so a future application is visible before
  // anyone has decided it — the manager's view deliberately doesn't do this,
  // because their register shows what happened, not what was asked for.
  const overlay = useMemo(() => buildMonthOverlay(
    month?.days || [],
    requests.filter((r) => r.status === 'pending')
  ), [month, requests]);

  const selectedType = types?.balances.find((b) => b.typeId === form.leaveTypeId);

  const apply = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await apiClient.post('/api/attendance/leave', form);
      toast.success(`Applied — it has gone to ${res.data.sentTo}`);
      setApplyOpen(false);
      setForm((f) => ({ ...f, reason: '' }));
      await load();
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Could not apply');
    } finally {
      setSaving(false);
    }
  };

  const cancel = async (r) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Withdraw your ${r.leaveTypeName} for ${fmtRange(r.fromDate, r.toDate)}?`)) return;
    try {
      await apiClient.post(`/api/attendance/leave/${r.id}/cancel`);
      toast.success('Withdrawn');
      await load();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not withdraw that');
    }
  };

  if (loading && !month) return <Card><Skeleton.Text lines={8} /></Card>;
  if (!settings) return null;

  const open = requests.filter((r) => ['pending', 'approved'].includes(r.status)
    && r.toDate >= todayISO());
  const past = requests.filter((r) => !open.includes(r));

  return (
    <div className="att-page">
      <Card>
        <div className="att-head">
          <div>
            <h3 className="att-head-title">My leave</h3>
            <p className="att-head-sub">
              {types?.approver?.anyManager
                ? 'Requests go to whichever manager picks them up.'
                : `Requests go to ${types?.approver?.name}.`}
            </p>
          </div>
          <div className="att-head-actions">
            <Button variant="primary" onClick={() => setApplyOpen(true)}>Apply for leave</Button>
          </div>
        </div>

        <div className="att-bal-strip">
          {(types?.balances || []).filter((b) => b.isActive).map((b) => (
            <div key={b.typeId} className={`att-bal${b.remaining !== null && b.remaining <= 0 ? ' is-out' : ''}`}>
              <span className="att-bal-name">{b.name}</span>
              {/* null quota means UNLIMITED, not zero — showing "0 left" for
                  unpaid leave would be exactly backwards. */}
              <span className="att-bal-num">{b.unlimited ? '∞' : `${b.remaining} left`}</span>
              <span className="att-bal-sub">
                {b.unlimited ? `${b.used} taken` : `${b.used} of ${b.quota} used`}
                {b.pending > 0 && ` · ${b.pending} awaiting`}
              </span>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <div className="att-head">
          <div>
            <h3 className="att-head-title">My month</h3>
            <p className="att-head-sub">
              Hover any day for what it was — worked hours, a holiday, or which leave you were on.
            </p>
          </div>
        </div>

        <WorkCalendar
          settings={settings}
          ym={ym}
          onMonthChange={setYm}
          overlay={overlay}
          showHolidayList={false}
          legendExtra={(
            <>
              <span><i className="att-key is-present" /> Present</span>
              <span><i className="att-key is-half" /> Half day</span>
              <span><i className="att-key is-absent" /> Absent</span>
              <span><i className="att-key is-leave" /> Leave</span>
              <span><i className="att-key is-pending" /> Awaiting approval</span>
              <span><i className="att-key is-holiday" /> Holiday</span>
              <span><i className="att-key is-off" /> Weekly off</span>
            </>
          )}
        />

        {month?.totals && (
          <p className="att-hint">
            {month.totals.workedLabel} worked against {month.totals.expectedLabel} expected
            {month.totals.leave > 0 && ` · ${month.totals.leave} day${month.totals.leave === 1 ? '' : 's'} leave`}
            {month.totals.absent > 0 && ` · ${month.totals.absent} absent`}
          </p>
        )}
      </Card>

      <Card>
        <h3 className="att-head-title" style={{ marginBottom: 10 }}>Requests</h3>
        {requests.length === 0 ? (
          <p className="att-empty">You haven&apos;t applied for any leave yet.</p>
        ) : (
          <>
            <ul className="att-lv-list">
              {[...open, ...past].map((r) => {
                const s = LEAVE_STATUS[r.status];
                // Withdrawable while pending, or approved but not yet started —
                // once it has begun it is history and a manager corrects it.
                const canCancel = r.status === 'pending'
                  || (r.status === 'approved' && r.fromDate > todayISO());
                return (
                  <li className="att-lv" key={r.id}>
                    <span className={`att-lv-s ${s.cls}`}>{s.label}</span>
                    <span className="att-lv-main">
                      <span className="att-lv-when">
                        {r.leaveTypeName} · {fmtRange(r.fromDate, r.toDate)}
                      </span>
                      <span className="att-lv-meta">
                        {r.dayCount} day{r.dayCount === 1 ? '' : 's'}
                        {r.approverName && ` · to ${r.approverName}`}
                        {r.decidedByName && ` · ${r.status} by ${r.decidedByName} on ${fmtDay(
                          new Date(r.decidedAt).toISOString().slice(0, 10)
                        )}`}
                      </span>
                      {r.reason && <span className="att-lv-reason">{r.reason}</span>}
                      {r.decisionNote && (
                        <span className="att-lv-reason"><b>Manager:</b> {r.decisionNote}</span>
                      )}
                    </span>
                    {canCancel && (
                      <span className="att-lv-actions">
                        <Button size="sm" variant="ghost" onClick={() => cancel(r)}>Withdraw</Button>
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </Card>

      <Modal
        isOpen={applyOpen}
        onClose={() => setApplyOpen(false)}
        title="Apply for leave"
        size="md"
      >
        <form onSubmit={apply} className="att-form">
          <div className="att-leave-grid">
            <div className="att-leave-wide">
              <Select
                label="Leave type"
                value={form.leaveTypeId}
                onChange={(e) => setForm({ ...form, leaveTypeId: e.target.value })}
                helperText={selectedType
                  ? (selectedType.unlimited
                    ? `${selectedType.name} is unlimited — ${selectedType.used} taken so far`
                    : `${selectedType.remaining} of ${selectedType.quota} left this year`)
                  : ''}
              >
                {(types?.balances || []).filter((b) => b.isActive).map((b) => (
                  <option key={b.typeId} value={b.typeId}>{b.name}</option>
                ))}
              </Select>
            </div>

            <Input
              label="From" type="date" required
              value={form.fromDate}
              onChange={(e) => {
                const fromDate = e.target.value;
                // Keep the range valid as you type rather than erroring later.
                setForm((f) => ({ ...f, fromDate, toDate: f.toDate < fromDate ? fromDate : f.toDate }));
              }}
            />
            <Input
              label="To" type="date" required
              min={form.fromDate}
              value={form.toDate}
              onChange={(e) => setForm({ ...form, toDate: e.target.value })}
            />

            <div className="att-leave-wide">
              <Select
                label="How much"
                value={form.portion}
                onChange={(e) => setForm({ ...form, portion: e.target.value })}
                disabled={form.fromDate !== form.toDate}
                helperText={form.fromDate !== form.toDate
                  ? 'A half day applies to a single date'
                  : 'Weekly offs and holidays in the range are not counted'}
              >
                {PORTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </Select>
            </div>

            <div className="att-leave-wide">
              <Input
                label="Reason"
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder={selectedType?.requiresReason ? 'Required' : 'Optional'}
                required={!!selectedType?.requiresReason}
              />
            </div>
          </div>

          <p className="att-hint" style={{ marginTop: 0 }}>
            This goes to {types?.approver?.anyManager ? 'whichever manager picks it up' : types?.approver?.name}.
            Nothing changes on your register until it is approved.
          </p>

          <div className="att-set-actions" style={{ justifyContent: 'flex-end' }}>
            <Button type="button" variant="ghost" onClick={() => setApplyOpen(false)}>Cancel</Button>
            <Button type="submit" variant="primary" loading={saving}>Send request</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default MyLeave;
